import {
  useState,
  useCallback,
  useEffect,
  useRef,
  useContext,
  useMemo,
} from "react";
import {
  AdvancedSearchConfigDto,
  BoostConfigDto,
  FuzzyConfigDto,
  MatchPhraseConfigDto,
  MatchPhrasePrefixConfigDto,
  ScriptScoreConfigDto,
} from "@swagger/typescript-fetch-goflyer";
import { adminApiService } from "../utils/api";
import { useAuthHeaders } from "./useAuthHeaders";
import { GoFlyerAdminContext } from "../contextApi/context";
import {
  setAdvancedSearchKeepSearchResults,
  setAdvancedSearchSearchLimit,
  setAdvancedSearchSearchFlyerItems,
} from "../contextApi/reducer";
import {
  safeLocalStorageGetJSON,
  safeLocalStorageHasItem,
  safeLocalStorageRemoveItem,
  safeLocalStorageSetJSON,
} from "../utils/localStorage";

/**
 * State interface for advanced search configuration
 */
export interface AdvancedSearchState {
  /** Whether advanced search features are enabled */
  isEnabled: boolean;
  /** Detailed configuration for advanced search behavior */
  config: AdvancedSearchConfigDto;
  /**
   * Whether to keep search results visible after creating a flyer item.
   * When false, search results are cleared automatically.
   * Managed in global context state.
   */
  keepSearchResults: boolean;
  /**
   * Maximum number of search results to display per page.
   * Used for pagination of image search results.
   * Managed in global context state.
   */
  searchLimit: number;
  /**
   * Whether to search in flyer item names (name and nameChinese fields).
   * When enabled, image search will also match against flyer item names.
   * Managed in global context state.
   */
  searchFlyerItems: boolean;
}

const DEFAULT_BOOST_CONFIG: BoostConfigDto = {
  wildcard: 1.0,
  fuzzy: 0.8,
  termExact: 2.0,
  termFirstWord: 1.5,
  termGeneral: 1.0,
  match: 1.2,
  prefix: 1.1,
  multiMatch: 1.0,
  matchPhrase: 1.0,
  matchPhrasePrefix: 1.0,
};

const DEFAULT_FUZZY_CONFIG: FuzzyConfigDto = {
  fuzziness: "AUTO",
  prefixLength: 0,
  maxExpansions: 50,
  transpositions: true,
};

const DEFAULT_MATCH_PHRASE_CONFIG: MatchPhraseConfigDto = {
  slop: 0,
};

const DEFAULT_MATCH_PHRASE_PREFIX_CONFIG: MatchPhrasePrefixConfigDto = {
  slop: 0,
  maxExpansions: 50,
};

const DEFAULT_SCRIPT_SCORE_CONFIG: ScriptScoreConfigDto = {
  enable: false,
  exactMatchMultiplier: 1,
  startsWithMultiplier: 1,
  containsBase: 0,
  combineMode: ScriptScoreConfigDto.CombineModeEnum?.Max ?? ("max" as any),
  boundaryBonus: 1,
};

const DEFAULT_CONFIG: AdvancedSearchConfigDto = {
  boostConfig: DEFAULT_BOOST_CONFIG,
  fuzzyConfig: DEFAULT_FUZZY_CONFIG,
  matchPhraseConfig: DEFAULT_MATCH_PHRASE_CONFIG,
  matchPhrasePrefixConfig: DEFAULT_MATCH_PHRASE_PREFIX_CONFIG,
  scriptScoreConfig: DEFAULT_SCRIPT_SCORE_CONFIG,
  boostMode: AdvancedSearchConfigDto.BoostModeEnum?.Multiply,
  minimumShouldMatch: 1,
};

// LocalStorage keys
const STORAGE_KEY = "goflyer_advanced_search_config";
const STORAGE_ENABLED_KEY = "goflyer_advanced_search_enabled";
const STORAGE_KEEP_SEARCH_RESULTS_KEY = "goflyer_keep_search_results";
const STORAGE_SEARCH_LIMIT_KEY = "goflyer_search_limit";
const STORAGE_SEARCH_FLYER_ITEMS_KEY = "goflyer_search_flyer_items";
const DEFAULT_SEARCH_LIMIT = 200;

const saveToStorage = <T>(key: string, value: T) => {
  safeLocalStorageSetJSON(key, value);
};

const loadFromStorage = <T>(key: string, defaultValue: T): T => {
  return safeLocalStorageGetJSON(key, defaultValue);
};

// Validate and merge configuration with defaults
const validateAndMergeConfig = (
  config: Partial<AdvancedSearchConfigDto> | null | undefined,
): AdvancedSearchConfigDto => {
  try {
    if (!config || typeof config !== "object") {
      return DEFAULT_CONFIG;
    }

    return {
      boostConfig: {
        ...DEFAULT_BOOST_CONFIG,
        ...(config.boostConfig || {}),
      },
      fuzzyConfig: {
        ...DEFAULT_FUZZY_CONFIG,
        ...(config.fuzzyConfig || {}),
      },
      matchPhraseConfig: {
        ...DEFAULT_MATCH_PHRASE_CONFIG,
        ...(config.matchPhraseConfig || {}),
      },
      matchPhrasePrefixConfig: {
        ...DEFAULT_MATCH_PHRASE_PREFIX_CONFIG,
        ...(config.matchPhrasePrefixConfig || {}),
      },
      scriptScoreConfig: {
        ...DEFAULT_SCRIPT_SCORE_CONFIG,
        ...(config.scriptScoreConfig || {}),
      },
      boostMode: config.boostMode ?? DEFAULT_CONFIG.boostMode,
      minimumShouldMatch:
        typeof config.minimumShouldMatch === "number"
          ? config.minimumShouldMatch
          : DEFAULT_CONFIG.minimumShouldMatch,
    };
  } catch (error) {
    console.warn("Failed to validate config, using defaults:", error);
    return DEFAULT_CONFIG;
  }
};

export const useAdvancedSearchConfig = () => {
  const authHeaders = useAuthHeaders();
  const { state: adminState, dispatch } = useContext(GoFlyerAdminContext);
  const fetchedOnceRef = useRef(false);
  const [serverDefaults, setServerDefaults] =
    useState<AdvancedSearchConfigDto | null>(null);
  const [isLoadingDefaults, setIsLoadingDefaults] = useState<boolean>(false);
  const [isHydrated, setIsHydrated] = useState<boolean>(false);

  const keepSearchResults =
    adminState.advancedSearchPreferences?.keepSearchResults ??
    loadFromStorage(STORAGE_KEEP_SEARCH_RESULTS_KEY, false);
  const searchLimit =
    adminState.advancedSearchPreferences?.searchLimit ??
    loadFromStorage(STORAGE_SEARCH_LIMIT_KEY, DEFAULT_SEARCH_LIMIT);
  const searchFlyerItems =
    adminState.advancedSearchPreferences?.searchFlyerItems ??
    loadFromStorage(STORAGE_SEARCH_FLYER_ITEMS_KEY, false);

  type AdvancedSearchCoreState = Omit<
    AdvancedSearchState,
    "keepSearchResults" | "searchLimit" | "searchFlyerItems"
  >;

  const [advancedSearchCoreState, setAdvancedSearchCoreState] =
    useState<AdvancedSearchCoreState>(() => {
      const savedConfig = loadFromStorage(STORAGE_KEY, DEFAULT_CONFIG);
      const savedEnabled = loadFromStorage(STORAGE_ENABLED_KEY, false);

      return {
        isEnabled: typeof savedEnabled === "boolean" ? savedEnabled : false,
        config: validateAndMergeConfig(savedConfig),
      };
    });

  const advancedSearchState: AdvancedSearchState = useMemo(
    () => ({
      ...advancedSearchCoreState,
      keepSearchResults,
      searchLimit,
      searchFlyerItems,
    }),
    [
      advancedSearchCoreState.isEnabled,
      advancedSearchCoreState.config,
      keepSearchResults,
      searchLimit,
      searchFlyerItems,
    ],
  );

  // Fetch default config from server once and merge with any saved config
  useEffect(() => {
    if (fetchedOnceRef.current) return;
    fetchedOnceRef.current = true;
    const fetchDefaults = async () => {
      try {
        setIsLoadingDefaults(true);
        const apiDefaults =
          await adminApiService.gfImageAdminControllerGetDefaultSearchConfig(
            authHeaders,
          );
        if (!apiDefaults) return;
        setServerDefaults(apiDefaults);

        const savedEnabled = loadFromStorage(STORAGE_ENABLED_KEY, false);
        const wasEnabled = typeof savedEnabled === "boolean" && savedEnabled;

        const hasSaved = safeLocalStorageHasItem(STORAGE_KEY);
        const savedConfig = hasSaved
          ? loadFromStorage(STORAGE_KEY, {} as AdvancedSearchConfigDto)
          : ({} as AdvancedSearchConfigDto);

        const merged = validateAndMergeConfig(
          hasSaved
            ? { ...apiDefaults, ...savedConfig }
            : apiDefaults,
        );

        setAdvancedSearchCoreState({
          isEnabled: wasEnabled,
          config: merged,
        });

        // Load keepSearchResults, searchLimit, and searchFlyerItems from localStorage
        const savedKeepSearchResults = loadFromStorage(
          STORAGE_KEEP_SEARCH_RESULTS_KEY,
          false,
        );
        const savedSearchLimit = loadFromStorage(
          STORAGE_SEARCH_LIMIT_KEY,
          DEFAULT_SEARCH_LIMIT,
        );
        const savedSearchFlyerItems = loadFromStorage(
          STORAGE_SEARCH_FLYER_ITEMS_KEY,
          false,
        );

        dispatch(setAdvancedSearchKeepSearchResults(savedKeepSearchResults));
        dispatch(setAdvancedSearchSearchLimit(savedSearchLimit));
        dispatch(setAdvancedSearchSearchFlyerItems(savedSearchFlyerItems));
      } catch (err) {
        setServerDefaults(null);
      } finally {
        setIsLoadingDefaults(false);
        setIsHydrated(true);
      }
    };
    fetchDefaults();
  }, [authHeaders, dispatch]);

  useEffect(() => {
    if (!isHydrated) return;
    saveToStorage(STORAGE_KEY, advancedSearchCoreState.config);
    saveToStorage(STORAGE_ENABLED_KEY, advancedSearchCoreState.isEnabled);
  }, [advancedSearchCoreState, isHydrated]);

  const updateBoostConfig = useCallback((updates: Partial<BoostConfigDto>) => {
    setAdvancedSearchCoreState((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        boostConfig: {
          ...prev.config.boostConfig,
          ...updates,
        },
      },
    }));
  }, []);

  const updateFuzzyConfig = useCallback((updates: Partial<FuzzyConfigDto>) => {
    setAdvancedSearchCoreState((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        fuzzyConfig: {
          ...prev.config.fuzzyConfig,
          ...updates,
        },
      },
    }));
  }, []);

  const updateMatchPhraseConfig = useCallback(
    (updates: Partial<MatchPhraseConfigDto>) => {
      setAdvancedSearchCoreState((prev) => ({
        ...prev,
        config: {
          ...prev.config,
          matchPhraseConfig: {
            ...prev.config.matchPhraseConfig,
            ...updates,
          },
        },
      }));
    },
    [],
  );

  const updateMatchPhrasePrefixConfig = useCallback(
    (updates: Partial<MatchPhrasePrefixConfigDto>) => {
      setAdvancedSearchCoreState((prev) => ({
        ...prev,
        config: {
          ...prev.config,
          matchPhrasePrefixConfig: {
            ...prev.config.matchPhrasePrefixConfig,
            ...updates,
          },
        },
      }));
    },
    [],
  );

  const updateMinimumShouldMatch = useCallback((value: number) => {
    setAdvancedSearchCoreState((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        minimumShouldMatch: value,
      },
    }));
  }, []);

  const updateScriptScoreConfig = useCallback(
    (updates: Partial<ScriptScoreConfigDto>) => {
      setAdvancedSearchCoreState((prev) => ({
        ...prev,
        config: {
          ...prev.config,
          scriptScoreConfig: {
            ...prev.config.scriptScoreConfig,
            ...updates,
          },
        },
      }));
    },
    [],
  );

  const updateBoostMode = useCallback(
    (mode?: AdvancedSearchConfigDto["boostMode"]) => {
      setAdvancedSearchCoreState((prev) => ({
        ...prev,
        config: {
          ...prev.config,
          boostMode: mode,
        },
      }));
    },
    [],
  );

  const toggleAdvancedSearch = useCallback((enabled: boolean) => {
    setAdvancedSearchCoreState((prev) => ({
      ...prev,
      isEnabled: enabled,
    }));
  }, []);

  const updateKeepSearchResults = useCallback(
    (keep: boolean) => {
      dispatch(setAdvancedSearchKeepSearchResults(keep));
      saveToStorage(STORAGE_KEEP_SEARCH_RESULTS_KEY, keep);
    },
    [dispatch],
  );

  const updateSearchLimit = useCallback(
    (limit: number) => {
      dispatch(setAdvancedSearchSearchLimit(limit));
      saveToStorage(STORAGE_SEARCH_LIMIT_KEY, limit);
    },
    [dispatch],
  );

  const updateSearchFlyerItems = useCallback(
    (search: boolean) => {
      dispatch(setAdvancedSearchSearchFlyerItems(search));
      saveToStorage(STORAGE_SEARCH_FLYER_ITEMS_KEY, search);
    },
    [dispatch],
  );

  const resetToDefaults = useCallback(() => {
    const configValues = validateAndMergeConfig(
      serverDefaults || DEFAULT_CONFIG,
    );
    setAdvancedSearchCoreState({
      isEnabled: false,
      config: configValues,
    });
    dispatch(setAdvancedSearchKeepSearchResults(false));
    dispatch(setAdvancedSearchSearchLimit(DEFAULT_SEARCH_LIMIT));
    dispatch(setAdvancedSearchSearchFlyerItems(false));

    safeLocalStorageRemoveItem(STORAGE_KEY);
    safeLocalStorageRemoveItem(STORAGE_ENABLED_KEY);
    safeLocalStorageRemoveItem(STORAGE_KEEP_SEARCH_RESULTS_KEY);
    safeLocalStorageRemoveItem(STORAGE_SEARCH_LIMIT_KEY);
    safeLocalStorageRemoveItem(STORAGE_SEARCH_FLYER_ITEMS_KEY);
  }, [dispatch, serverDefaults]);

  const getSearchConfig = useCallback(():
    | AdvancedSearchConfigDto
    | undefined => {
    if (!advancedSearchState.isEnabled) {
      return undefined;
    }

    const config = advancedSearchState.config;
    const cleanConfig: AdvancedSearchConfigDto = {};

    // Only include boost config if any values are set
    if (config.boostConfig && Object.keys(config.boostConfig).length > 0) {
      const boostConfig: BoostConfigDto = {};
      if (config.boostConfig.wildcard !== undefined)
        boostConfig.wildcard = config.boostConfig.wildcard;
      if (config.boostConfig.fuzzy !== undefined)
        boostConfig.fuzzy = config.boostConfig.fuzzy;
      if (config.boostConfig.termExact !== undefined)
        boostConfig.termExact = config.boostConfig.termExact;
      if (config.boostConfig.termFirstWord !== undefined)
        boostConfig.termFirstWord = config.boostConfig.termFirstWord;
      if (config.boostConfig.termGeneral !== undefined)
        boostConfig.termGeneral = config.boostConfig.termGeneral;
      if (config.boostConfig.match !== undefined)
        boostConfig.match = config.boostConfig.match;
      if (config.boostConfig.prefix !== undefined)
        boostConfig.prefix = config.boostConfig.prefix;
      if (config.boostConfig.multiMatch !== undefined)
        boostConfig.multiMatch = config.boostConfig.multiMatch;
      if (config.boostConfig.matchPhrase !== undefined)
        boostConfig.matchPhrase = config.boostConfig.matchPhrase;
      if (config.boostConfig.matchPhrasePrefix !== undefined)
        boostConfig.matchPhrasePrefix = config.boostConfig.matchPhrasePrefix;

      if (Object.keys(boostConfig).length > 0) {
        cleanConfig.boostConfig = boostConfig;
      }
    }

    // Only include fuzzy config if any values are set
    if (config.fuzzyConfig && Object.keys(config.fuzzyConfig).length > 0) {
      const fuzzyConfig: FuzzyConfigDto = {};
      if (config.fuzzyConfig.fuzziness !== undefined)
        fuzzyConfig.fuzziness = config.fuzzyConfig.fuzziness;
      if (config.fuzzyConfig.prefixLength !== undefined)
        fuzzyConfig.prefixLength = config.fuzzyConfig.prefixLength;
      if (config.fuzzyConfig.maxExpansions !== undefined)
        fuzzyConfig.maxExpansions = config.fuzzyConfig.maxExpansions;
      if (config.fuzzyConfig.transpositions !== undefined)
        fuzzyConfig.transpositions = config.fuzzyConfig.transpositions;

      if (Object.keys(fuzzyConfig).length > 0) {
        cleanConfig.fuzzyConfig = fuzzyConfig;
      }
    }

    // Only include match phrase config if any values are set
    if (
      config.matchPhraseConfig &&
      Object.keys(config.matchPhraseConfig).length > 0
    ) {
      const matchPhraseConfig: MatchPhraseConfigDto = {};
      if (config.matchPhraseConfig.slop !== undefined)
        matchPhraseConfig.slop = config.matchPhraseConfig.slop;

      if (Object.keys(matchPhraseConfig).length > 0) {
        cleanConfig.matchPhraseConfig = matchPhraseConfig;
      }
    }

    // Only include match phrase prefix config if any values are set
    if (
      config.matchPhrasePrefixConfig &&
      Object.keys(config.matchPhrasePrefixConfig).length > 0
    ) {
      const matchPhrasePrefixConfig: MatchPhrasePrefixConfigDto = {};
      if (config.matchPhrasePrefixConfig.slop !== undefined)
        matchPhrasePrefixConfig.slop = config.matchPhrasePrefixConfig.slop;
      if (config.matchPhrasePrefixConfig.maxExpansions !== undefined)
        matchPhrasePrefixConfig.maxExpansions =
          config.matchPhrasePrefixConfig.maxExpansions;

      if (Object.keys(matchPhrasePrefixConfig).length > 0) {
        cleanConfig.matchPhrasePrefixConfig = matchPhrasePrefixConfig;
      }
    }

    // Only include minimum should match if it's set
    if (config.minimumShouldMatch !== undefined) {
      cleanConfig.minimumShouldMatch = config.minimumShouldMatch;
    }

    // Script score config
    if (config.scriptScoreConfig) {
      const {
        enable,
        exactMatchMultiplier,
        startsWithMultiplier,
        containsBase,
        combineMode,
        maxBoostCap,
        boundaryBonus,
      } = config.scriptScoreConfig as ScriptScoreConfigDto;
      const scriptScoreConfig: ScriptScoreConfigDto = {};
      if (enable !== undefined) scriptScoreConfig.enable = enable;
      if (exactMatchMultiplier !== undefined)
        scriptScoreConfig.exactMatchMultiplier = exactMatchMultiplier;
      if (startsWithMultiplier !== undefined)
        scriptScoreConfig.startsWithMultiplier = startsWithMultiplier;
      if (containsBase !== undefined)
        scriptScoreConfig.containsBase = containsBase;
      if (combineMode !== undefined)
        scriptScoreConfig.combineMode = combineMode;
      if (maxBoostCap !== undefined)
        scriptScoreConfig.maxBoostCap = maxBoostCap;
      if (boundaryBonus !== undefined)
        scriptScoreConfig.boundaryBonus = boundaryBonus;
      if (Object.keys(scriptScoreConfig).length)
        cleanConfig.scriptScoreConfig = scriptScoreConfig;
    }

    if (config.boostMode !== undefined) {
      cleanConfig.boostMode = config.boostMode;
    }

    return Object.keys(cleanConfig).length > 0 ? cleanConfig : undefined;
  }, [advancedSearchState]);

  const exportConfig = useCallback(() => {
    return {
      isEnabled: advancedSearchState.isEnabled,
      config: advancedSearchState.config,
      keepSearchResults: advancedSearchState.keepSearchResults,
      searchLimit: advancedSearchState.searchLimit,
      searchFlyerItems: advancedSearchState.searchFlyerItems,
      timestamp: new Date().toISOString(),
    };
  }, [advancedSearchState]);

  const importConfig = useCallback(
    (importedData: {
      isEnabled: boolean;
      config: AdvancedSearchConfigDto;
      keepSearchResults?: boolean;
      searchLimit?: number;
      searchFlyerItems?: boolean;
    }) => {
      const mergedConfig = { ...DEFAULT_CONFIG, ...importedData.config };
      setAdvancedSearchCoreState({
        isEnabled: importedData.isEnabled,
        config: mergedConfig,
      });
      saveToStorage(STORAGE_KEY, mergedConfig);
      saveToStorage(STORAGE_ENABLED_KEY, importedData.isEnabled);
      if (typeof importedData.keepSearchResults === "boolean") {
        dispatch(
          setAdvancedSearchKeepSearchResults(importedData.keepSearchResults),
        );
        saveToStorage(STORAGE_KEEP_SEARCH_RESULTS_KEY, importedData.keepSearchResults);
      }
      if (typeof importedData.searchLimit === "number") {
        dispatch(setAdvancedSearchSearchLimit(importedData.searchLimit));
        saveToStorage(STORAGE_SEARCH_LIMIT_KEY, importedData.searchLimit);
      }
      if (typeof importedData.searchFlyerItems === "boolean") {
        dispatch(setAdvancedSearchSearchFlyerItems(importedData.searchFlyerItems));
        saveToStorage(STORAGE_SEARCH_FLYER_ITEMS_KEY, importedData.searchFlyerItems);
      }
    },
    [dispatch],
  );

  const hasUnsavedChanges = useCallback(() => {
    const savedConfig = loadFromStorage(STORAGE_KEY, DEFAULT_CONFIG);
    const savedEnabled = loadFromStorage(STORAGE_ENABLED_KEY, false);

    return (
      JSON.stringify(savedConfig) !==
        JSON.stringify(advancedSearchState.config) ||
      savedEnabled !== advancedSearchState.isEnabled
    );
  }, [advancedSearchState]);

  return {
    advancedSearchState,
    serverDefaults,
    isLoadingDefaults,
    updateBoostConfig,
    updateFuzzyConfig,
    updateMatchPhraseConfig,
    updateMatchPhrasePrefixConfig,
    updateMinimumShouldMatch,
    updateScriptScoreConfig,
    updateBoostMode,
    toggleAdvancedSearch,
    updateKeepSearchResults,
    updateSearchLimit,
    updateSearchFlyerItems,
    resetToDefaults,
    getSearchConfig,
    isAdvancedEnabled: advancedSearchState.isEnabled,
    config: advancedSearchState.config,
    keepSearchResults: advancedSearchState.keepSearchResults,
    searchLimit: advancedSearchState.searchLimit,
    searchFlyerItems: advancedSearchState.searchFlyerItems,
    exportConfig,
    importConfig,
    hasUnsavedChanges,
  };
};
